import { isReceivedDispatchStatus } from '../utils/receivedDispatchStatus';
import DispatchPaidFlag from './DispatchPaidFlag';
import LabelPrintDialog from './LabelPrintDialog';
import { paginateDispatchInvoice } from '../utils/paginateDispatchInvoice';
import DraftDispatchEditor from './DraftDispatchEditor';
import { downloadCsv } from '../utils/bulkCsv';
import { filterDispatchOrderDates } from '../utils/dispatchOrderFilters';
import { getDispatchPurchaseRows, PURCHASE_REQUEST_HEADERS } from '../utils/dispatchPurchaseRequest';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { API_BASE_URL } from '../utils/apiConfig';
import { RefreshCw } from 'lucide-react';

import {
  updateInventoryDispatchStatus,
  updateInventoryDispatchOrderItems,
  receiveDispatchToStakeholder,
  completeInternalPackingDispatch,
  deleteInventoryDispatchOrder,
  fetchInventoryDispatchOrders,
  syncInventoryDispatchStock,
  fetchInventoryProducts,
  fetchStockTransactions,
} from '../features/inventory/stockManagerInventorySlice';
import DispatchItemsTable, { getDispatchTableRows, dispatchLineTotal, dispatchGrandTotal } from './DispatchItemsTable';
import {
  getPackingLabelRowKey,
  getPackingLabelRows,
  printPackingLabels,
  summarizePackingLabelRows,
  selectPackingLabelRows,
} from '../utils/packingLabelPrint';
import {
  formatDispatchDate,
  getDispatchItemBarcode,
  getDispatchItemBrand,
  getCalculatedPackingAmounts,
  getDispatchItemCategory,
  getDispatchItemPackingConfigurations,
  getDispatchItemProductName,
  getDispatchItemUnit,
  getPackingConfigUnit,
  getPackingConfigurationsFromNotes,
} from '../utils/dispatchDisplay';

const DispatchOrdersSection = ({
  orders = [],
  loading = false,
  catalogBarcodes = [],
  onRefresh = null,
  inventoryProducts = [],
  onCreate = null,
  initialDispatchOrderId = '',
}) => {
  const [editingDraft, setEditingDraft] = React.useState(null);
  const dispatch = useDispatch();
  const token = useSelector(state => state.posUser?.userInfo?.token || '');
  const labelPricingInFlight = React.useRef(false);
  const [labelDialog, setLabelDialog] = React.useState(null);
  const [excludedLabelKeys, setExcludedLabelKeys] = React.useState(new Set());
  const [labelCounts, setLabelCounts] = React.useState({});
  let selectedLabelCount = 0;
  let labelCountError = '';
  if (labelDialog) {
    try {
      selectedLabelCount = selectPackingLabelRows(labelDialog.rows, labelCounts, excludedLabelKeys).length;
    } catch (error) { labelCountError = error.message; }
  }
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [searchText, setSearchText] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [orderLimit, setOrderLimit] = React.useState('10');
  const [expandedOrderId, setExpandedOrderId] = React.useState('');
  const openedLinkedOrder = React.useRef('');
  React.useEffect(() => {
    if (!initialDispatchOrderId || openedLinkedOrder.current === initialDispatchOrderId) return;
    const linked = orders.find(order => String(order.id) === String(initialDispatchOrderId));
    if (!linked) return;
    openedLinkedOrder.current = initialDispatchOrderId;
    setSearchText(linked.dispatch_no || '');
    setExpandedOrderId(String(linked.id));
    setStatusFilter('all');
    setDateFrom(''); setDateTo('');
  }, [orders, initialDispatchOrderId]);
  const [stockSync, setStockSync] = React.useState({});
  const stockSyncInFlight = React.useRef(new Set());

  const syncStockAndPrices = async (order) => {
    const id = String(order.id);
    if (stockSyncInFlight.current.has(id)) return;
    stockSyncInFlight.current.add(id);
    setStockSync(current => ({ ...current, [id]: { loading: true } }));
    try {
      const updated = await dispatch(syncInventoryDispatchStock(order.id)).unwrap();
      const shortages = getDispatchPurchaseRows(updated).length;
      const unknown = (updated.items || []).some(item => item.stock_status === 'unknown');
      setStockSync(current => ({ ...current, [id]: { loading: false,
        message: unknown ? 'Stock availability could not be determined. Check the source warehouse.' :
          shortages ? 'Stock and prices refreshed. Some items still need purchasing.' : 'Stock and prices refreshed.',
        error: unknown } }));
      setExpandedOrderId(id);
    } catch (error) {
      setStockSync(current => ({ ...current, [id]: { loading: false, error: true,
        message: 'Sync failed: ' + (error?.message || String(error)) } }));
    } finally {
      stockSyncInFlight.current.delete(id);
    }
  };

  const refreshOrders = React.useCallback(async () => {
    if (onRefresh) {
      await onRefresh();
      return;
    }

    await Promise.all([
      dispatch(fetchInventoryDispatchOrders()),
      dispatch(fetchInventoryProducts()),
      dispatch(fetchStockTransactions()),
    ]);
  }, [dispatch, onRefresh]);

  React.useEffect(() => {
    const refreshVisible = () => {
      if (document.visibilityState === 'visible') refreshOrders().catch(() => {});
    };
    window.addEventListener('focus', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      window.removeEventListener('focus', refreshVisible);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [refreshOrders]);

  const normalizeUnit = (value) =>
    String(value || '').toLowerCase().replace(/\./g, '').replace(/\s+/g, '');

  const getUnitQtyInGrams = (quantity, unit) => {
    const qty = Number(quantity || 0);
    const normalizedUnit = normalizeUnit(unit);

    if (!qty) return 0;
    if (normalizedUnit.startsWith('kg')) return qty * 1000;
    if (
      normalizedUnit.startsWith('gm') ||
      normalizedUnit.startsWith('gms') ||
      normalizedUnit === 'g'
    ) {
      return qty;
    }

    return qty;
  };

  const getSourceItemGrams = (item) => {
    const unitParts = String(getDispatchItemUnit(item)).split(/\s+/);
    const quantity = item.barcode_quantity || item.quantity || unitParts[0];
    const unit =
      item.unit_short_code ||
      item.unit_code ||
      item.unit_name ||
      unitParts.slice(1).join('');

    return getUnitQtyInGrams(quantity, unit);
  };

  const getPackingConfigGrams = (config) =>
    getUnitQtyInGrams(
      config.barcode_quantity || config.quantity,
      config.unit_short_code || config.unit_code || config.unit_name
    ) * Number(config.pack_count || 0);

  const getCorrectedInternalPackingUnits = (item) => {
    const configs =
      getDispatchItemPackingConfigurations(item).length > 0
        ? getDispatchItemPackingConfigurations(item)
        : getPackingConfigurationsFromNotes(item);
    const sourceGrams = getSourceItemGrams(item);
    const configuredGrams = configs.reduce(
      (sum, config) => sum + getPackingConfigGrams(config),
      0
    );

    if (!sourceGrams || !configuredGrams) return Number(item.no_of_units || item.qty || 0);

    return Number((configuredGrams / sourceGrams).toFixed(3));
  };

  const updateInternalPackingSourceQuantities = async (order) => {
    const items = (order.items || []).map((item) => {
      const correctedUnits = getCorrectedInternalPackingUnits(item);
      const packingConfigurations =
        getDispatchItemPackingConfigurations(item).length > 0
          ? getDispatchItemPackingConfigurations(item)
          : item.packing_configurations ||
            item.packingConfigurations ||
            item.packing_configs ||
            item.packingConfigs ||
            [];

      return {
        ...item,
        inventory_product_id: Number(item.inventory_product_id || item.id),
        product_barcode_id: Number(item.product_barcode_id),
        qty: correctedUnits,
        no_of_units: correctedUnits,
        exp_date: item.exp_date,
        notes: item.notes || null,
        packing_configurations: packingConfigurations,
      };
    });

    const shouldUpdate = items.some(
      (item, index) =>
        Number(item.no_of_units || 0) !==
        Number(order.items?.[index]?.no_of_units || order.items?.[index]?.qty || 0)
    );

    if (!shouldUpdate) return;

    await dispatch(
      updateInventoryDispatchOrderItems({
        id: order.id,
        items,
      })
    ).unwrap();
  };

  const getDestinationType = (order) =>
    String(order?.destination || '').split(':')[0].trim().toLowerCase();

  const getRouteName = (route = '') => {
    const parts = String(route || '').split(':');
    return parts.slice(2).join(':') || parts[1] || route || '';
  };

  const isOutletDispatch = (order) => getDestinationType(order) === 'outlet';

  const isStakeholderDispatch = (order) =>
    ['vendor', 'customer', 'stakeholder'].includes(getDestinationType(order));

  const isInternalPackingDispatch = (order) =>
    getDestinationType(order) === 'internal_packing';

  const isCompletedStatus = (status) =>
    isReceivedDispatchStatus(status) || String(status || '').toLowerCase() === 'dispatched';

  const statusUpdatesInFlight = React.useRef(new Set());
  const markStatus = async (id, status) => {
    if (statusUpdatesInFlight.current.has(String(id))) return;
    statusUpdatesInFlight.current.add(String(id));
    try {
      await dispatch(
        updateInventoryDispatchStatus({
          id,
          dispatch_status: status,
        })
      ).unwrap();

      await refreshOrders();
    } catch (error) {
      await refreshOrders().catch(() => {});
      alert(error?.message || error || 'Failed to update dispatch status');
    } finally {
      statusUpdatesInFlight.current.delete(String(id));
    }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm('Delete this draft dispatch?')) return;

    try {
      await dispatch(deleteInventoryDispatchOrder(id)).unwrap();
      dispatch(fetchInventoryDispatchOrders());
    } catch (error) {
      alert(error?.message || error || 'Failed to delete dispatch');
    }
  };

  const receiveStakeholderDispatch = async (order) => {
    if (
      !window.confirm(
        `Receive dispatch ${order.dispatch_no} by stakeholder? This will reduce inventory stock.`
      )
    ) {
      return;
    }

    try {
      await dispatch(
        receiveDispatchToStakeholder({
          dispatchOrderId: order.id,
        })
      ).unwrap();

      dispatch(fetchInventoryDispatchOrders());
      dispatch(fetchInventoryProducts());
      dispatch(fetchStockTransactions());
    } catch (error) {
      alert(error?.message || error || 'Failed to receive dispatch by stakeholder');
    }
  };

  const firstValue = (...values) =>
    values.find((value) => value !== undefined && value !== null && value !== '');

  const getCatalogBarcode = (productBarcodeId) =>
    (catalogBarcodes || []).find(
      (barcode) =>
        String(
          barcode.id ||
            barcode.product_barcode_id ||
            barcode.productBarcodeId ||
            barcode.catalogProductBarcodeId ||
            barcode.catalogProductBarcodeID
        ) ===
        String(productBarcodeId)
    ) || {};

  const getRealMkBarcode = (...values) =>
    values
      .map((value) => String(value || '').trim())
      .find((value) => /^\d{8,}$/.test(value)) || '';

  const getProductBarcodeId = (...values) =>
    values
      .map((value) => String(value || '').trim())
      .find((value) => /^\d+$/.test(value)) || '';

  const getBarcodeUnitFields = (barcode = {}, fallback = {}) => ({
    barcode_quantity: firstValue(
      fallback.barcode_quantity,
      fallback.quantity,
      barcode.barcode_quantity,
      barcode.quantity
    ),
    quantity: firstValue(
      fallback.barcode_quantity,
      fallback.quantity,
      barcode.barcode_quantity,
      barcode.quantity
    ),
    unit_short_code: firstValue(
      fallback.unit_short_code,
      fallback.unit_code,
      fallback.unit_name,
      fallback.units,
      fallback.unit,
      barcode.unit_short_code,
      barcode.unit_code,
      barcode.unit_name,
      barcode.units,
      barcode.unit
    ),
    unit_name: firstValue(
      fallback.unit_name,
      fallback.unit_short_code,
      fallback.unit_code,
      fallback.units,
      fallback.unit,
      barcode.unit_name,
      barcode.unit_short_code,
      barcode.unit_code,
      barcode.units,
      barcode.unit
    ),
  });

  const getLabelReadyOrder = (order) => ({
    ...order,
    items: (order.items || []).map((item) => {
      const packingConfigurations = getDispatchItemPackingConfigurations(item);

      if (!packingConfigurations.length) {
        const productBarcodeId = getProductBarcodeId(
          item.product_barcode_id,
          item.productBarcodeId,
          item.catalogProductBarcodeId,
          item.catalogProductBarcodeID,
          item.label_id,
          item.labelId,
          item.mkid
        );
        const catalogBarcode = getCatalogBarcode(
          productBarcodeId
        );
        const postgresBarcodeId = getProductBarcodeId(
          productBarcodeId,
          catalogBarcode.id,
          catalogBarcode.product_barcode_id,
          catalogBarcode.productBarcodeId,
          catalogBarcode.catalogProductBarcodeId,
          catalogBarcode.catalogProductBarcodeID,
          catalogBarcode.mkid
        );
        const mkBarcode = getRealMkBarcode(
          catalogBarcode.mk_barcode,
          catalogBarcode.barcode,
          item.mk_barcode,
          item.barcode,
          item.bar_code
        );
        const unitFields = getBarcodeUnitFields(catalogBarcode, item);

        return mkBarcode
          ? {
              ...item,
              ...unitFields,
              dprice: firstValue(item.dprice, item.discounted_price, item.selling_price, catalogBarcode.dprice, catalogBarcode.discounted_price),
              mrp_amount: firstValue(item.mrp_amount, item.unit_mrp, item.mrp, catalogBarcode.mrp_amount, catalogBarcode.unit_mrp, catalogBarcode.mrp),
              product_barcode_id: postgresBarcodeId,
              productBarcodeId: postgresBarcodeId,
              catalogProductBarcodeId: postgresBarcodeId,
              catalogProductBarcodeID: postgresBarcodeId,
              label_id: postgresBarcodeId,
              labelId: postgresBarcodeId,
              mk_barcode: mkBarcode,
              barcode: mkBarcode,
              mkid: postgresBarcodeId,
            }
          : item;
      }

      return {
        ...item,
        packing_configurations: packingConfigurations.map((config) => {
          const productBarcodeId = getProductBarcodeId(
            config.product_barcode_id,
            config.productBarcodeId,
            config.catalogProductBarcodeId,
            config.catalogProductBarcodeID,
            config.label_id,
            config.labelId,
            config.mkid
          );
          const catalogBarcode = getCatalogBarcode(
            productBarcodeId
          );
          const postgresBarcodeId = getProductBarcodeId(
            productBarcodeId,
            catalogBarcode.id,
            catalogBarcode.product_barcode_id,
            catalogBarcode.productBarcodeId,
            catalogBarcode.catalogProductBarcodeId,
            catalogBarcode.catalogProductBarcodeID,
            catalogBarcode.mkid
          );
          const mkBarcode = getRealMkBarcode(
            catalogBarcode.mk_barcode,
            catalogBarcode.barcode,
            config.mk_barcode,
            config.barcode,
            config.bar_code,
            item.mk_barcode,
            item.barcode,
            item.bar_code
          );
          const unitFields = getBarcodeUnitFields(catalogBarcode, config);

          return mkBarcode
            ? {
                ...config,
                ...unitFields,
                dprice: firstValue(config.dprice, config.discounted_price, config.selling_price, catalogBarcode.dprice, catalogBarcode.discounted_price),
                mrp_amount: firstValue(config.mrp_amount, config.unit_mrp, config.mrp, catalogBarcode.mrp_amount, catalogBarcode.unit_mrp, catalogBarcode.mrp),
                product_barcode_id: postgresBarcodeId,
                productBarcodeId: postgresBarcodeId,
                catalogProductBarcodeId: postgresBarcodeId,
                catalogProductBarcodeID: postgresBarcodeId,
                label_id: postgresBarcodeId,
                labelId: postgresBarcodeId,
                mk_barcode: mkBarcode,
                barcode: mkBarcode,
                mkid: postgresBarcodeId,
              }
            : config;
        }),
      };
    }),
  });

  const getReceivePricePayload = (order) =>
    (order.items || []).flatMap((item) => {
      const configs =
        getDispatchItemPackingConfigurations(item).length > 0
          ? getDispatchItemPackingConfigurations(item)
          : getPackingConfigurationsFromNotes(item);

      if (!configs.length) {
        const catalogBarcode =
          catalogBarcodes.find(
            (barcode) =>
              String(barcode.id || barcode.product_barcode_id) ===
              String(item.product_barcode_id)
          ) || {};
        const mkBarcode =
          item.mk_barcode || item.MK_BARCODE || item.bar_code || catalogBarcode.mk_barcode || '';
        const vendorBarcode =
          item.vendor_barcode || catalogBarcode.barcode ||
          (item.barcode && String(item.barcode) !== String(mkBarcode) ? item.barcode : '');
        const itemDprice = Math.ceil(Number(
          firstValue(
            item.dprice,
            item.discount_price,
            item.discounted_price,
            item.selling_price,
            item.unit_price,
            item.price,
            item.package_amount,
            0
          )
        ));

        const itemMrp = Number(firstValue(
          item.unit_mrp,
          item.mrp_amount,
          item.mrp,
          item.price,
          0
        ));

        return [
          {
            dispatch_order_item_id: item.id,
            product_barcode_id: item.product_barcode_id,
            MK_BARCODE: mkBarcode,
            mk_barcode: mkBarcode,
            vendor_barcode: vendorBarcode,
            barcode: vendorBarcode,
            image_url: item.image_url || item.imageUrl || '',
            qty: Number(item.qty || item.no_of_units || 0),
            no_of_units: Number(item.no_of_units || item.qty || 0),
            price: itemMrp,
            unit_mrp: itemMrp,
            dprice: itemDprice,
            selling_price: itemDprice,
          },
        ];
      }

      return configs.map((config) => {
        const catalogBarcode =
          catalogBarcodes.find(
            (barcode) =>
              String(barcode.id || barcode.product_barcode_id) ===
              String(config.product_barcode_id)
          ) || {};
        const mergedConfig = {
          ...catalogBarcode,
          ...config,
          barcode_quantity:
            config.barcode_quantity ||
            catalogBarcode.quantity ||
            catalogBarcode.barcode_quantity,
          unit_short_code:
            config.unit_short_code || catalogBarcode.unit_short_code || catalogBarcode.unit_name,
        };
        const calculatedAmounts = getCalculatedPackingAmounts(item, mergedConfig);
        const mkBarcode =
          config.mk_barcode || config.MK_BARCODE || config.bar_code || catalogBarcode.mk_barcode || '';
        const vendorBarcode =
          config.vendor_barcode || catalogBarcode.barcode ||
          (config.barcode && String(config.barcode) !== String(mkBarcode) ? config.barcode : '');

        const configDprice = Math.ceil(Number(
          firstValue(
            calculatedAmounts.dprice,
            config.dprice,
            config.discount_price,
            config.discounted_price,
            config.selling_price,
            config.package_amount,
            0
          )
        ));
        const configMrp = Number(firstValue(
          config.unit_mrp,
          config.mrp_amount,
          config.mrp,
          calculatedAmounts.mrpAmount,
          0
        ));

        return {
          dispatch_order_item_id: item.id,
          product_barcode_id: config.product_barcode_id,
          MK_BARCODE: mkBarcode,
          mk_barcode: mkBarcode,
          vendor_barcode: vendorBarcode,
          barcode: vendorBarcode,
          image_url: config.image_url || config.imageUrl || item.image_url || item.imageUrl || '',
          qty: Number(config.pack_count || config.qty || 0),
          no_of_units: Number(config.pack_count || config.qty || 0),
          price: configMrp,
          unit_mrp: configMrp,
          dprice: configDprice,
          selling_price: configDprice,
        };
      });
    });

  const loadCurrentLabelOrder = async order => {
    const response = await fetch(`${API_BASE_URL}/accounts/dispatch-label-pricing/${order.id}`, {
      headers: { Authorization: `Bearer ${token}` }, cache: 'no-store',
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Unable to calculate current label prices.');
    return getLabelReadyOrder(data);
  };

  const openLabelDialog = async (order, shouldMarkDone = false) => {
    if (labelPricingInFlight.current) return;
    labelPricingInFlight.current = true;
    try {
    const labelReadyOrder = await loadCurrentLabelOrder(order);
    const rows = getPackingLabelRows(labelReadyOrder);

    if (!rows.length) {
      alert('No labels available for this dispatch.');
      return;
    }

    setExcludedLabelKeys(new Set());
    setLabelCounts({});
    setLabelDialog({
      order,
      labelReadyOrder,
      rows,
      shouldMarkDone,
    });
    } catch (error) { alert(error.message || 'Unable to prepare labels.'); }
    finally { labelPricingInFlight.current = false; }
  };

  const toggleExcludedLabel = (key) => {
    setExcludedLabelKeys((current) => {
      const next = new Set(current);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  };

  const setAllLabelsIncluded = (included) => {
    if (!labelDialog) return;

    if (included) {
      setExcludedLabelKeys(new Set());
      return;
    }

    setExcludedLabelKeys(
      new Set(summarizePackingLabelRows(labelDialog.rows).map((row) => row.key))
    );
  };

  const runLabelAction = async (mode) => {
    if (!labelDialog || labelPricingInFlight.current) return;
    labelPricingInFlight.current = true;

    try {
      const currentOrder = await loadCurrentLabelOrder(labelDialog.order);
      const currentRows = getPackingLabelRows(currentOrder);
      const signature = rows => JSON.stringify(rows.map(row => [getPackingLabelRowKey(row), row.dprice, row.mrp_amount]));
      const changed = signature(currentRows) !== signature(labelDialog.rows);
      let currentExcludedKeys = excludedLabelKeys;
      let currentCounts = labelCounts;
      if (changed) {
        const identity = row => JSON.stringify([row.product_barcode_id || row.label_id,
          row.mk_barcode || row.barcode, row.product_name || row.product_name_eng,
          row.barcode_quantity || row.quantity, row.unit_short_code || row.unit_name]);
        const excludedIdentities = new Set(labelDialog.rows.filter(row => excludedLabelKeys.has(getPackingLabelRowKey(row))).map(identity));
        currentExcludedKeys = new Set(currentRows.filter(row => excludedIdentities.has(identity(row))).map(getPackingLabelRowKey));
        const countsByIdentity = new Map(labelDialog.rows
          .filter(row => labelCounts[getPackingLabelRowKey(row)] !== undefined)
          .map(row => [identity(row), labelCounts[getPackingLabelRowKey(row)]]));
        currentCounts = Object.fromEntries(currentRows
          .filter(row => countsByIdentity.has(identity(row)))
          .map(row => [getPackingLabelRowKey(row), countsByIdentity.get(identity(row))]));
        setLabelCounts(currentCounts);
        setExcludedLabelKeys(currentExcludedKeys);
        setLabelDialog(previous => ({ ...previous, labelReadyOrder: currentOrder, rows: currentRows }));
      }
      if (mode !== 'preview' && changed) {
        alert('Prices changed since the preview. Review the updated DPrice and MRP, then print the replacement labels.');
        return;
      }
      const completed = await printPackingLabels(currentOrder, {
        mode,
        excludedRowKeys: Array.from(currentExcludedKeys),
        rows: selectPackingLabelRows(currentRows, currentCounts, currentExcludedKeys),
      });

      if (!completed || mode === 'preview') return;

      if (labelDialog.shouldMarkDone) {
        await dispatch(
          updateInventoryDispatchStatus({
            id: labelDialog.order.id,
            dispatch_status: 'label_printed',
          })
        ).unwrap();

        dispatch(fetchInventoryDispatchOrders());
      }

      setLabelDialog(null);
    } catch (error) {
      alert(error?.message || error || 'Labels printed, but status update failed');
    } finally { labelPricingInFlight.current = false; }
  };

  const completeInternalPacking = async (order) => {
    if (
      !window.confirm(
        `Dispatch ${order.dispatch_no} to internal packing? This will reduce source inventory and add the new packing stock.`
      )
    ) {
      return;
    }

    try {
      const status = String(order.dispatch_status || '').toLowerCase();

      await updateInternalPackingSourceQuantities(order);

      if (status === 'packed') {
        await dispatch(
          updateInventoryDispatchStatus({
            id: order.id,
            dispatch_status: 'label_printed',
          })
        ).unwrap();
      }

      await dispatch(
        completeInternalPackingDispatch({
          dispatchOrderId: order.id,
        })
      ).unwrap();

      dispatch(fetchInventoryDispatchOrders());
      dispatch(fetchInventoryProducts());
      dispatch(fetchStockTransactions());
    } catch (error) {
      alert(error?.message || error || 'Failed to complete internal packing dispatch');
    }
  };

  const getStatusLabel = (status, order = null) => {
    const normalized = String(status || '').toLowerCase();

    if (normalized === 'received_to_warehouse') return `RECEIVED IN ${getRouteName(order?.source) || 'WAREHOUSE'}`;
    if (normalized === 'received_to_outlet') return 'RECEIVED TO OUTLET';
    if (normalized === 'received_by_stakeholder' && isInternalPackingDispatch(order)) {
      return `RECEIVED IN ${getRouteName(order?.source) || 'WAREHOUSE'}`;
    }
    if (normalized === 'received_by_stakeholder') return 'RECEIVED BY STAKEHOLDER';
    if (normalized === 'sent') return 'SENT';
    if (normalized === 'packed') return 'PACKED';
    if (normalized === 'label_printed') return 'LABEL PRINTED';
    if (normalized === 'dispatched') return 'DISPATCHED';

    return normalized.toUpperCase();
  };

  const printDispatch = (order) => {
    const getPrintPrice = (value) => {
      if (value === undefined || value === null || value === '') return '-';
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric.toFixed(2) : String(value);
    };

    const getPrintPercent = (value) => {
      if (value === undefined || value === null || value === '') return '-';
      const numeric = Number(value);
      return Number.isFinite(numeric) ? `${numeric.toFixed(2)}%` : String(value);
    };

    const printRows = getDispatchTableRows(order.items || [], catalogBarcodes);
    const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]));
    const rows = printRows.map((item, index) => `<tr data-line-total="${Number(dispatchLineTotal(item)) || 0}">
      <td class="text-center">${index + 1}</td><td>${escape(item.printProduct === '-' ? item.product : item.printProduct || item.product)}</td><td>${escape(item.brand)}</td>
      <td class="text-center">${escape(item.qty)}</td><td class="text-center">${escape(item.unit)}</td>
      <td class="text-right">${item.pricingAvailable === false ? '-' : getPrintPrice(item.mrp)}</td>
      <td class="text-right">${item.pricingAvailable === false ? '-' : getPrintPercent(item.discountPercent)}</td>
      <td class="text-right">${item.pricingAvailable === false ? '-' : getPrintPrice(item.dprice)}</td>
      <td class="text-right">${getPrintPrice(dispatchLineTotal(item))}</td>
    </tr>`).join('');

    const html = `
      <html>
        <head>
          <title>${order.dispatch_no || 'Dispatch Print'}</title>
          <style>
            @page {
              size: A5 portrait;
              margin: 8mm;
              margin-top: 10px;
            }

            * {
              box-sizing: border-box;
            }

            body {
              font-family: Arial, 'Nirmala UI', 'Noto Sans Telugu', sans-serif;
              margin: 0;
              padding: 0;
              width: 100%;
              max-width: 132mm;
              font-size: 8pt;
              color: #111827;
            }

            h2 {
              margin: 0 0 4px 0;
              font-size: 16pt;
            }

            .subtitle {
              margin-bottom: 10px;
              color: #475569;
              font-size: 12px;
            }

            .meta {
              display: grid;
              grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
              overflow-wrap: anywhere;
              gap: 5px 10px;
              margin-bottom: 10px;
              padding: 7px;
              border: 1px solid #d1d5db;
              border-radius: 8px;
              background: #f8fafc;
            }

            table {
              width: 100%;
              table-layout: fixed;
              border-collapse: collapse;
              margin-top: 12px;
              font-size: 8pt;
              line-height: 1.3;
            }

            th,
            td {
              border: 1px solid #333;
              padding: 4px 2px;
              overflow-wrap: anywhere;
              text-align: left;
              vertical-align: top;
            }

            th {
              background: #f1f5f9;
              font-weight: bold;
            }

            .text-center {
              text-align: center;
            }

            .text-right {
              text-align: right;
            }

            .footer {
              margin-top: 28px;
              display: grid;
              grid-template-columns: repeat(3, minmax(0, 1fr));
              gap: 12px;
              font-size: 8pt;
              font-weight: bold;
              break-inside: avoid;
            }

            .signature-line {
              display: block;
              border-bottom: 1px solid #333;
              height: 24px;
            }

            thead {
              display: table-header-group;
            }
            .dispatch-print-page { display: flow-root; break-after: page; page-break-after: always; }
            .dispatch-print-page:last-of-type { break-after: auto; page-break-after: auto; }
            .invoice-grand-total { margin-top: 10px; text-align: right; font-weight: bold; border-top: 2px solid #333; padding-top: 6px; }
            [data-dispatch-ending] { break-inside: avoid; }
            .page-number { text-align: right; margin-top: 8px; font-size: 7pt; }

            tr, .meta {
              break-inside: avoid;
            }

            @media screen {
              body {
                margin: 20px auto;
              }
            }

            @media print {
              body {
                width: 100%;
                margin: 0;
                padding: 0;
              }

              button {
                display: none;
              }
            }
          </style>
        </head>

        <body>
          <div data-dispatch-header>
          <h2>ManaKirana</h2>
          <div class="shop-details" style="font-size:8pt;line-height:1.4;margin-bottom:6px">
            <div>GST No: 37AAUCM3501B1ZN</div>
            <div>H.No. 5-85, near Laxmi Cheruvu, Amalapuram, Uppalaguptam,</div>
            <div>Dr BR Ambedkar Konaseema District, Andhra Pradesh - 533222</div>
            <div>Phone: 81217 74325 | Landline: 08856-297898</div>
            <div>Email: customercare@manakirana.online</div>
          </div>
          <div class="subtitle">Inventory Dispatch</div>

          <div class="meta">
            <div><b>Dispatch No:</b> ${order.dispatch_no || '-'}</div>
            <div><b>Status:</b> ${getStatusLabel(order.dispatch_status, order)}</div>
            <div><b>Source:</b> ${order.source || '-'}</div>
            <div><b>Destination:</b> ${order.destination || '-'}</div>
            <div><b>Expected:</b> ${
              order.expected_dispatch_at
                ? new Date(order.expected_dispatch_at).toLocaleString()
                : '-'
            }</div>
          </div>

          </div>
          <table data-dispatch-table>
            <colgroup><col style="width: 5%" /><col style="width: 25%" /><col style="width: 12%" /><col style="width: 6%" /><col style="width: 9%" /><col style="width: 11%" /><col style="width: 10%" /><col style="width: 11%" /><col style="width: 11%" /></colgroup>
            <thead>
              <tr>
                <th class="text-center">S.No</th>
                <th>Product</th>
                <th>Brand</th>
                <th class="text-center">Qty</th>
                <th class="text-center">Unit</th>
                <th class="text-right">MRP</th>
                <th class="text-right">Discount %</th>
                <th class="text-right">D.Price</th>
                <th class="text-right">Total Price</th>
              </tr>
            </thead>
            <tbody>
              ${
                rows ||
                `<tr>
                  <td colspan="9" class="text-center">No items</td>
                </tr>`
              }
            </tbody>
          </table>
          <div data-dispatch-ending>
          <div class="invoice-grand-total">Grand total: ${getPrintPrice(dispatchGrandTotal(printRows))}</div>
          <div class="footer">
            <div>Packed By:<span class="signature-line"></span></div>
            <div>Label Printed By:<span class="signature-line"></span></div>
            <div>Dispatched By:<span class="signature-line"></span></div>
          </div>
          </div>
          <script>
            window.onload = function () {
              (${paginateDispatchInvoice.toString()})(document);
              window.print();
              window.onafterprint = function () {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=1000,height=700');

    if (!printWindow) {
      alert('Please allow popups to print dispatch.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const statusSummary = React.useMemo(
    () =>
      orders.reduce((acc, order) => {
        const status = String(order.dispatch_status || 'unknown').toLowerCase();
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {}),
    [orders]
  );

  const statusOptions = React.useMemo(
    () =>
      Object.keys(statusSummary)
        .sort()
        .map((status) => ({
          value: status,
          label: getStatusLabel(status),
          count: statusSummary[status],
        })),
    [statusSummary]
  );

  const filteredOrders = React.useMemo(() => {
    const needle = String(searchText || '').trim().toLowerCase();

    return filterDispatchOrderDates(orders, dateFrom, dateTo).filter((order) => {
      const status = String(order.dispatch_status || '').toLowerCase();
      const statusMatch = statusFilter === 'all' || status === statusFilter;
      const searchMatch =
        !needle ||
        [
          order.dispatch_no,
          order.source,
          order.destination,
          order.dispatch_notes,
          ...(order.items || []).flatMap((item) => [
            getDispatchItemProductName(item),
            getDispatchItemBrand(item),
            getDispatchItemBarcode(item),
          ]),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(needle);

      return statusMatch && searchMatch;
    });
  }, [orders, searchText, statusFilter, dateFrom, dateTo]);
  const visibleOrders = orderLimit === 'all' ? filteredOrders : filteredOrders.slice(0, Number(orderLimit));

  const getOrderItemSummary = (order) => {
    const items = order.items || [];
    const totalUnits = items.reduce(
      (sum, item) => sum + Number(item.no_of_units || item.qty || 0),
      0
    );

    return {
      itemCount: items.length,
      totalUnits,
      firstProduct: items[0] ? getDispatchItemProductName(items[0]) : '',
    };
  };

  return (
    <section className="overflow-hidden rounded-xl border bg-white shadow-sm">
      {onCreate && <button type="button" onClick={onCreate} className="m-4 rounded bg-blue-600 px-4 py-2 font-semibold text-white">Create dispatch</button>}
      {editingDraft && <DraftDispatchEditor order={editingDraft} inventoryProducts={inventoryProducts}
        onClose={() => setEditingDraft(null)} onSave={async items => {
          await dispatch(updateInventoryDispatchOrderItems({ id: editingDraft.id, items })).unwrap();
          dispatch(fetchInventoryDispatchOrders());
        }} />}
      <div className="border-b bg-white px-4 py-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Track Dispatch Orders
            </h2>
            <p className="text-sm text-gray-500">
              Update status, print dispatch copies, print labels and receive stock.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-[auto_180px_minmax(220px,320px)]">
            <button
              type="button"
              onClick={refreshOrders}
              disabled={loading}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              {loading ? 'Refreshing' : 'Refresh'}
            </button>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm"
            >
              <option value="all">All Statuses ({orders.length})</option>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} ({option.count})
                </option>
              ))}
            </select>
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="h-10 rounded-lg border border-gray-300 px-3 text-sm"
              placeholder="Search dispatch, product, barcode"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-xs font-semibold text-gray-600">From date
            <input type="date" value={dateFrom} max={dateTo || undefined} onChange={e => setDateFrom(e.target.value)} className="mt-1 block h-10 rounded-lg border px-3 text-sm" />
          </label>
          <label className="text-xs font-semibold text-gray-600">To date
            <input type="date" value={dateTo} min={dateFrom || undefined} onChange={e => setDateTo(e.target.value)} className="mt-1 block h-10 rounded-lg border px-3 text-sm" />
          </label>
          <label className="text-xs font-semibold text-gray-600">Show dispatches
            <select value={orderLimit} onChange={e => setOrderLimit(e.target.value)} className="mt-1 block h-10 rounded-lg border bg-white px-3 text-sm">
              <option value="10">Latest 10</option><option value="25">Latest 25</option><option value="50">Latest 50</option><option value="all">All matching</option>
            </select>
          </label>
          <button type="button" onClick={() => { setDateFrom(''); setDateTo(''); setStatusFilter('all'); setSearchText(''); setOrderLimit('10'); }} className="h-10 rounded-lg border px-3 text-sm">Reset filters</button>
          <p className="text-xs text-gray-500">Showing {visibleOrders.length} of {filteredOrders.length} matching dispatches. Newest first; dates use creation date, or expected dispatch date when unavailable.</p>
        </div>
        {dateFrom && dateTo && dateFrom > dateTo && <p role="alert" className="mt-2 text-sm text-red-600">From date must be on or before To date.</p>}
      </div>

      {loading && (
        <div className="py-6 text-center text-gray-500">
          Loading dispatch orders...
        </div>
      )}

      {!loading && visibleOrders.length === 0 && (
        <div className="m-4 rounded-xl border border-dashed bg-slate-50 p-8 text-center text-gray-500">
          No dispatch orders found
        </div>
      )}

      <div className="space-y-4 p-4">
        {visibleOrders.map((order) => {
          const status = String(order.dispatch_status || '').toLowerCase();
          const summary = getOrderItemSummary(order);
          const expanded = String(expandedOrderId) === String(order.id);

          return (
            <article key={order.id} className="overflow-hidden rounded-xl border bg-white shadow-sm">
              <div className="grid gap-3 p-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-bold text-gray-900">{order.dispatch_no}</h3>
                    <StatusBadge status={order.dispatch_status} order={order} />
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                      {summary.itemCount} item{summary.itemCount === 1 ? '' : 's'}
                    </span>
                  </div>

                  <div className="mt-3 grid gap-2 text-sm text-gray-600 md:grid-cols-2">
                    <div className="min-w-0 rounded-lg bg-slate-50 px-3 py-2">
                      <div className="text-xs font-bold uppercase text-gray-400">Source</div>
                      <div className="whitespace-normal break-words font-semibold text-gray-800">{order.source || '-'}</div>
                    </div>
                    <div className="min-w-0 rounded-lg bg-slate-50 px-3 py-2">
                      <div className="text-xs font-bold uppercase text-gray-400">Destination</div>
                      <div className="whitespace-normal break-words font-semibold text-gray-800">{order.destination || '-'}</div>
                    </div>
                    <div className="rounded-lg bg-slate-50 px-3 py-2">
                      <div className="text-xs font-bold uppercase text-gray-400">Expected</div>
                      <div className="font-semibold text-gray-800">
                        {order.expected_dispatch_at
                          ? new Date(order.expected_dispatch_at).toLocaleString()
                          : '-'}
                      </div>
                    </div>
                    <div className="rounded-lg bg-slate-50 px-3 py-2">
                      <div className="text-xs font-bold uppercase text-gray-400">Units</div>
                      <div className="font-semibold text-gray-800">
                        {Number(summary.totalUnits || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>

                  {order.dispatch_notes && (
                    <p className="mt-1 text-sm text-gray-500">
                      Notes: {order.dispatch_notes}
                    </p>
                  )}

                  {summary.firstProduct ? (
                    <p className="mt-2 text-xs text-gray-500">
                      First item: {summary.firstProduct}
                    </p>
                  ) : null}
                </div>

                <div className="flex flex-wrap justify-start gap-2 lg:max-w-[520px] lg:justify-end">
                  {status === 'draft' && <button type="button" disabled={loading}
                    onClick={() => { dispatch(fetchInventoryProducts()); setEditingDraft(order); }}
                    className="rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white">Edit draft</button>}
                  {['draft', 'sent', 'packed'].includes(status) && isOutletDispatch(order) && (
                    <button type="button"
                      onClick={() => syncStockAndPrices(order)}
                      disabled={loading || stockSync[String(order.id)]?.loading}
                      className="inline-flex items-center gap-2 rounded-lg border border-blue-600 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50">
                      <RefreshCw size={16} className={stockSync[String(order.id)]?.loading ? 'animate-spin' : ''} />
                      {stockSync[String(order.id)]?.loading ? 'Syncing…' : 'Sync Stock & Prices'}
                    </button>
                  )}
                  {getDispatchPurchaseRows(order).length > 0 && (
                    <button type="button"
                      onClick={() => downloadCsv(
                        'purchase_request_' + (order.dispatch_no || order.id) + '.csv',
                        PURCHASE_REQUEST_HEADERS, getDispatchPurchaseRows(order))}
                      title="Only shortage quantities. Review suggested supplier and purchase price; fill any blank fields before importing in Create Purchase Orders."
                      className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800">
                      Download Purchase Template
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() =>
                      setExpandedOrderId((current) =>
                        String(current) === String(order.id) ? '' : String(order.id)
                      )
                    }
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    {expanded ? 'Hide Items' : 'View Items'}
                  </button>

                  <button
                    type="button"
                    onClick={() => printDispatch(order)}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    Print
                  </button>

                  {!['cancelled', 'deleted'].includes(status) && (
                      <button
                        type="button"
                        onClick={() => openLabelDialog(order, false)}
                        className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700"
                      >
                        Label Print
                      </button>
                    )}

                  {isStakeholderDispatch(order) && ['sent','packed','dispatched','received_by_stakeholder'].includes(status) && <DispatchPaidFlag order={order} token={token} />}

                  {status === 'draft' && (
                    <>
                      <button
                        type="button"
                        onClick={() => markStatus(order.id, 'sent')}
                        className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700"
                      >
                        Mark Sent
                      </button>

                      <button
                        type="button"
                        onClick={() => deleteOrder(order.id)}
                        className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </>
                  )}

                  {status === 'sent' && (
                    <>
                      <button
                        type="button"
                        onClick={() => markStatus(order.id, 'packed')}
                        className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700"
                      >
                        Mark Packed
                      </button>
                    </>
                  )}

                  {status === 'packed' &&
                    (isInternalPackingDispatch(order) ? (
                      <button
                        type="button"
                        onClick={() => completeInternalPacking(order)}
                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                      >
                        Dispatch Packings
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => markStatus(order.id, 'dispatched')}
                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                      >
                        Mark Dispatched
                      </button>
                    ))}

                  {status === 'label_printed' && isInternalPackingDispatch(order) && (
                    <button
                      type="button"
                      onClick={() => completeInternalPacking(order)}
                      className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                    >
                      Dispatch Packings
                    </button>
                  )}

                  {status === 'dispatched' &&
                    (isOutletDispatch(order) ? (
                      <span className="rounded-lg border border-green-300 bg-green-50 px-4 py-2 text-sm font-bold text-green-700">
                        Waiting POS Receive
                      </span>
                    ) : isStakeholderDispatch(order) ? (
                      <button
                        type="button"
                        onClick={() => receiveStakeholderDispatch(order)}
                        className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
                      >
                        Receive By Stakeholder
                      </button>
                    ) : isInternalPackingDispatch(order) ? (
                      <button
                        type="button"
                        onClick={() => markStatus(order.id, 'received_by_stakeholder')}
                        className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
                      >
                        Mark Received In {getRouteName(order.source) || 'Warehouse'}
                      </button>
                    ) : (
                      <span className="rounded-lg border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm font-bold text-indigo-700">
                        Dispatched To {getDestinationType(order) || 'Destination'}
                      </span>
                    ))}

                  {!['cancelled'].includes(status) && !isCompletedStatus(status) && (
                    <button
                      type="button"
                      onClick={() => markStatus(order.id, 'cancelled')}
                      className="rounded-lg bg-gray-700 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>

              {stockSync[String(order.id)]?.message && (
                <p role="status" className={'px-4 pb-3 text-sm ' + (stockSync[String(order.id)].error ? 'text-red-700' : 'text-emerald-700')}>
                  {stockSync[String(order.id)].message}
                </p>
              )}
              {expanded ? (
                <div className="border-t bg-slate-50 p-4">
                  {(order.items || []).some(item => item.pricing_preview) && (
                    <p className="mb-3 text-xs text-gray-600">Stock and estimated prices are from the source warehouse. Final prices depend on the batch selected for dispatch. Purchase templates contain shortage quantities; review suggested supplier/prices and fill any blanks before importing.</p>
                  )}
                  <div className="overflow-x-auto rounded-xl border bg-white">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-100 text-gray-700">
                        <tr>
                          <th className="min-w-[280px] w-[320px] px-3 py-2 text-left">Product</th>
                          <th className="px-3 py-2 text-left">Barcode</th>
                          <th className="px-3 py-2 text-left">Category</th>
                          <th className="px-3 py-2 text-left">Brand</th>
                          <th className="px-3 py-2 text-center">Qty</th>
                          <th className="px-3 py-2 text-center">Unit</th>
                          <th className="px-3 py-2 text-left">Expiry</th>
                          <th className="px-3 py-2 text-right">Unit Price</th>
                          <th className="px-3 py-2 text-right">MRP</th>
                          <th className="px-3 py-2 text-right">Discount %</th>
                          <th className="px-3 py-2 text-right">D.Price</th>
                          <th className="px-3 py-2 text-right">Total Price</th>
                          <th className="px-3 py-2 text-left">Notes</th>
                        </tr>
                      </thead>

                      <DispatchItemsTable
                        items={order.items || []}
                        catalogBarcodes={catalogBarcodes}
                      />
                    </table>
                  </div>
                </div>
              ) : null}
            </article>
          );
        })}
      </div>

      {labelDialog && (
        <LabelPrintDialog
          order={labelDialog.order}
          rows={summarizePackingLabelRows(labelDialog.rows)}
          excludedKeys={excludedLabelKeys}
          selectedCount={selectedLabelCount}
          counts={labelCounts}
          countError={labelCountError}
          onCountChange={(key, value) => setLabelCounts(current => ({ ...current, [key]: value }))}
          onToggle={toggleExcludedLabel}
          onIncludeAll={() => setAllLabelsIncluded(true)}
          onExcludeAll={() => setAllLabelsIncluded(false)}
          onPreview={() => runLabelAction('preview')}
          onPrint={() => runLabelAction('print')}
          onClose={() => setLabelDialog(null)}
        />
      )}
    </section>
  );
};

const StatusBadge = ({ status, order }) => {
  const normalized = String(status || 'draft').toLowerCase();
  const destinationType = String(order?.destination || '')
    .split(':')[0]
    .trim()
    .toLowerCase();
  const sourceName =
    String(order?.source || '').split(':').slice(2).join(':') ||
    String(order?.source || '').split(':')[1] ||
    'WAREHOUSE';

  const cls =
    normalized === 'draft'
      ? 'bg-yellow-100 text-yellow-800'
      : normalized === 'sent'
      ? 'bg-purple-100 text-purple-800'
      : normalized === 'packed'
      ? 'bg-orange-100 text-orange-800'
      : normalized === 'label_printed'
      ? 'bg-sky-100 text-sky-800'
      : normalized === 'dispatched'
      ? 'bg-indigo-100 text-indigo-800'
      : normalized === 'received_to_outlet'
      ? 'bg-green-100 text-green-800'
      : normalized === 'received_by_stakeholder'
      ? 'bg-green-100 text-green-800'
      : normalized === 'cancelled'
      ? 'bg-red-100 text-red-800'
      : 'bg-gray-100 text-gray-800';

  const label =
    normalized === 'received_to_outlet'
      ? 'RECEIVED TO OUTLET'
      : normalized === 'received_by_stakeholder' && destinationType === 'internal_packing'
      ? `RECEIVED IN ${sourceName}`
      : normalized === 'received_by_stakeholder'
      ? 'RECEIVED BY STAKEHOLDER'
      : normalized === 'label_printed'
      ? 'LABEL PRINTED'
      : normalized.toUpperCase();

  return (
    <span className={`rounded-full px-3 py-1 text-xs font-bold ${cls}`}>
      {label}
    </span>
  );
};

export default DispatchOrdersSection;


